// /var/www/oszczedzam/client/src/components/transactions/TransactionList.js
import React, { useState, useMemo } from 'react';
import { 
  Box, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, TableSortLabel, Paper, 
  IconButton, Chip, Typography, Tooltip, TablePagination,
  Checkbox, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem,
  LinearProgress, Alert
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Event as EventIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { visuallyHidden } from '@mui/utils';

// Lista kategorii
const allCategories = [
  'Mieszkanie', 'Transport', 'Żywność', 'Rozrywka', 'Zdrowie', 
  'Odzież', 'Edukacja', 'Prezenty', 'Podróże', 'Dzieci', 'Sport', 
  'Zwierzęta', 'Elektronika', 'Rachunki', 'Subskrypcje', 'Inne',
  'Wynagrodzenie', 'Inwestycje', 'Premia', 'Sprzedaż'
];

function TransactionList({ 
  transactions = [], 
  onEdit, 
  onDelete, 
  onBulkCategoryChange, 
  loading = false 
}) {
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('date');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selected, setSelected] = useState([]);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [previewTransaction, setPreviewTransaction] = useState(null);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);

  // Memoized validation i sorting
  const validatedTransactions = useMemo(() => {
    if (!Array.isArray(transactions)) return [];
    
    return transactions.filter(t => {
      // Sprawdzenie czy transakcja ma wymagane pola
      return (
        t && 
        typeof t === 'object' && 
        t.id && 
        t.title && 
        t.date && 
        (t.amount !== undefined && t.amount !== null && !isNaN(t.amount))
      );
    });
  }, [transactions]);

  // Obsługa sortowania
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    // Reset selection przy zmianie sortowania
    setSelected([]);
  };

  // Funkcja porównująca dla sortowania
  const descendingComparator = (a, b, orderBy) => {
    if (!a || !b) return 0;
    
    let aValue = a[orderBy];
    let bValue = b[orderBy];
    
    // Sprawdzenie, czy wartości istnieją
    if (aValue === undefined && bValue === undefined) return 0;
    if (aValue === undefined) return 1;
    if (bValue === undefined) return -1;
    
    // Specjalne traktowanie dla dat
    if (orderBy === 'date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    // Specjalne traktowanie dla kwot (porównanie bezwzględne)
    if (orderBy === 'amount') {
      aValue = Math.abs(aValue);
      bValue = Math.abs(bValue);
    }
    
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Sortowanie stabilne
  const stabilizedSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  // Przygotowanie posortowanych danych
  const sortedTransactions = useMemo(() => {
    return stabilizedSort(validatedTransactions, getComparator(order, orderBy));
  }, [validatedTransactions, order, orderBy]);

  // Paginacja
  const paginatedTransactions = useMemo(() => {
    return sortedTransactions.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedTransactions, page, rowsPerPage]);

  // Obsługa zmiany strony
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSelected([]); // Reset selection przy zmianie strony
  };

  // Obsługa zmiany liczby wierszy na stronie
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setSelected([]); // Reset selection
  };

  // Obsługa zaznaczania jednego wiersza
  const handleSelectRow = (id) => {
    if (!id) return;
    
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter(itemId => itemId !== id);
    }

    setSelected(newSelected);
  };

  // Obsługa zaznaczania wszystkich wierszy na bieżącej stronie
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = paginatedTransactions
        .map(t => t?.id)
        .filter(Boolean);
      setSelected(prev => {
        // Dodaj nowe ID, unikając duplikatów
        const combined = [...prev, ...newSelecteds];
        return [...new Set(combined)];
      });
      return;
    }
    
    // Usuń tylko ID z bieżącej strony
    const currentPageIds = paginatedTransactions.map(t => t?.id).filter(Boolean);
    setSelected(prev => prev.filter(id => !currentPageIds.includes(id)));
  };

  // Sprawdź ile wierszy na bieżącej stronie jest zaznaczonych
  const currentPageSelected = paginatedTransactions.filter(t => 
    t?.id && selected.includes(t.id)
  ).length;

  // Obsługa zmiany kategorii dla zaznaczonych transakcji
  const handleCategoryChange = () => {
    if (selected.length > 0 && selectedCategory) {
      // Sprawdź czy wszystkie wybrane ID rzeczywiście istnieją
      const validSelectedIds = selected.filter(id => 
        validatedTransactions.some(t => t.id === id)
      );
      
      if (validSelectedIds.length === 0) {
        return;
      }
      
      onBulkCategoryChange(validSelectedIds, selectedCategory);
      setSelected([]);
      setSelectedCategory('');
      setOpenCategoryDialog(false);
    }
  };

  // Sprawdź czy wiersz jest zaznaczony
  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Otwórz podgląd transakcji
  const handlePreviewTransaction = (transaction) => {
    setPreviewTransaction(transaction);
    setOpenPreviewDialog(true);
  };

  // Nagłówki kolumn
  const headCells = [
    { id: 'select', label: '', disableSorting: true },
    { id: 'date', label: 'Data' },
    { id: 'title', label: 'Tytuł' },
    { id: 'category', label: 'Kategoria' },
    { id: 'tags', label: 'Tagi', disableSorting: true },
    { id: 'amount', label: 'Kwota' },
    { id: 'actions', label: 'Akcje', disableSorting: true }
  ];

  // Formatowanie daty
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
      return new Date(dateString).toLocaleDateString('pl-PL', options);
    } catch (error) {
      console.error("Błąd formatowania daty:", error);
      return dateString;
    }
  };

  // Formatowanie kwoty
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) return '';
    try {
      return amount.toLocaleString('pl-PL', {
        style: 'currency',
        currency: 'PLN'
      });
    } catch (error) {
      console.error("Błąd formatowania kwoty:", error);
      return `${amount} zł`;
    }
  };

  // Skróć tekst
  const truncateText = (text, maxLength = 30) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Jeśli brak transakcji
  if (validatedTransactions.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Brak transakcji do wyświetlenia
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Dodaj swoją pierwszą transakcję lub dostosuj filtry wyszukiwania.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Wskaźnik ładowania dla masowych operacji */}
      {loading && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Przyciski akcji dla zaznaczonych transakcji */}
      {selected.length > 0 && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'action.selected', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1">
              Zaznaczono {selected.length} transakcji
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                size="small"
                onClick={() => setOpenCategoryDialog(true)}
                disabled={loading}
              >
                Zmień kategorię
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setSelected([])}
                disabled={loading}
              >
                Anuluj zaznaczenie
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                    align={headCell.id === 'select' ? 'center' : 'left'}
                    padding={headCell.id === 'select' ? 'checkbox' : 'normal'}
                  >
                    {headCell.id === 'select' ? (
                      <Checkbox
                        color="primary"
                        indeterminate={currentPageSelected > 0 && currentPageSelected < paginatedTransactions.length}
                        checked={paginatedTransactions.length > 0 && currentPageSelected === paginatedTransactions.length}
                        onChange={handleSelectAllClick}
                        disabled={loading}
                      />
                    ) : headCell.disableSorting ? (
                      headCell.label
                    ) : (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTransactions.map((transaction) => {
                if (!transaction || !transaction.id) return null;
                
                const isItemSelected = isSelected(transaction.id);
                
                return (
                  <TableRow
                    hover
                    key={transaction.id}
                    selected={isItemSelected}
                    sx={{ opacity: loading ? 0.6 : 1 }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onChange={() => handleSelectRow(transaction.id)}
                        disabled={loading}
                      />
                    </TableCell>
                    <TableCell>{formatDate(transaction.date)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {truncateText(transaction.title, 25)}
                          </Typography>
                          {transaction.description && (
                            <Typography variant="caption" color="text.secondary">
                              {truncateText(transaction.description, 40)}
                            </Typography>
                          )}
                        </Box>
                        {transaction.isRecurring && (
                          <Tooltip title="Transakcja cykliczna">
                            <EventIcon fontSize="small" color="action" sx={{ ml: 1 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={transaction.category || 'Brak kategorii'} 
                        size="small" 
                        variant="outlined"
                        color={transaction.category ? 'default' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {transaction.tags && Array.isArray(transaction.tags) && transaction.tags.length > 0 ? (
                          transaction.tags.slice(0, 2).map((tag) => (
                            <Chip key={tag} label={tag} size="small" />
                          ))
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                        {transaction.tags && transaction.tags.length > 2 && (
                          <Chip 
                            label={`+${transaction.tags.length - 2}`} 
                            size="small" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: (transaction.amount || 0) >= 0 ? 'success.main' : 'error.main',
                          fontWeight: 'medium'
                        }}
                      >
                        {formatAmount(transaction.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Podgląd">
                          <IconButton
                            size="small"
                            onClick={() => handlePreviewTransaction(transaction)}
                            disabled={loading}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edytuj">
                          <IconButton
                            size="small"
                            onClick={() => onEdit(transaction)}
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Usuń">
                          <IconButton
                            size="small"
                            onClick={() => onDelete(transaction.id)}
                            disabled={loading}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={validatedTransactions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Wierszy na stronie:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} z ${count !== -1 ? count : `więcej niż ${to}`}`
          }
        />
      </Paper>

      {/* Dialog zmiany kategorii */}
      <Dialog 
        open={openCategoryDialog} 
        onClose={() => setOpenCategoryDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Zmień kategorię dla {selected.length} zaznaczonych transakcji
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="bulk-category-label">Nowa kategoria</InputLabel>
            <Select
              labelId="bulk-category-label"
              id="bulk-category"
              value={selectedCategory}
              label="Nowa kategoria"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {allCategories.map((category) => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>Anuluj</Button>
          <Button 
            onClick={handleCategoryChange} 
            variant="contained" 
            disabled={!selectedCategory || loading}
          >
            Zmień kategorię
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog podglądu transakcji */}
      <Dialog 
        open={openPreviewDialog} 
        onClose={() => setOpenPreviewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Szczegóły transakcji</DialogTitle>
        <DialogContent>
          {previewTransaction && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {previewTransaction.title}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Kwota</Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: previewTransaction.amount >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 'bold'
                  }}
                >
                  {formatAmount(previewTransaction.amount)}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">Data</Typography>
              <Typography variant="body1" gutterBottom>
                {formatDate(previewTransaction.date)}
              </Typography>

              <Typography variant="body2" color="text.secondary">Kategoria</Typography>
              <Typography variant="body1" gutterBottom>
                {previewTransaction.category || 'Brak kategorii'}
              </Typography>

              {previewTransaction.description && (
                <>
                  <Typography variant="body2" color="text.secondary">Opis</Typography>
                  <Typography variant="body1" gutterBottom>
                    {previewTransaction.description}
                  </Typography>
                </>
              )}

              {previewTransaction.tags && previewTransaction.tags.length > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">Tagi</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {previewTransaction.tags.map(tag => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Box>
                </>
              )}

              {previewTransaction.isRecurring && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Ta transakcja jest oznaczona jako cykliczna
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreviewDialog(false)}>Zamknij</Button>
          <Button 
            onClick={() => {
              setOpenPreviewDialog(false);
              onEdit(previewTransaction);
            }} 
            variant="contained"
          >
            Edytuj
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TransactionList;